import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { InactivityWarningDialogComponent } from '../../components/inactivity-warning-dialog/inactivity-warning-dialog.component';
import {
  DEFAULT_INACTIVITY_LOGOUT_CONFIG,
  INACTIVITY_LOGOUT_CONFIG,
  InactivityLogoutConfig,
} from './inactivity-logout.config';

export type InactivityLogoutStatus = 'stopped' | 'monitoring' | 'warning';

/** Runs on the owning tab before inactivity `onLogout`; may be sync or async. */
export type InactivityBeforeLogoutHandler = () => void | Promise<void>;

export const INACTIVITY_STORAGE_PREFIX = 'et.inactivity.';

export const INACTIVITY_STORAGE_KEYS = {
  lastActivityAt: `${INACTIVITY_STORAGE_PREFIX}lastActivityAt`,
  warningShownAt: `${INACTIVITY_STORAGE_PREFIX}warningShownAt`,
  leaderTabId: `${INACTIVITY_STORAGE_PREFIX}leaderTabId`,
  logoutInProgress: `${INACTIVITY_STORAGE_PREFIX}logoutInProgress`,
} as const;

const ACTIVITY_EVENTS = [
  'pointerdown',
  'keydown',
  'touchstart',
  'click',
  'scroll',
] as const;

@Injectable({
  providedIn: 'root',
})
export class InactivityLogoutService {
  private readonly dialog = inject(MatDialog);
  private readonly injectedConfig = inject(INACTIVITY_LOGOUT_CONFIG);

  private readonly config: Required<
    Pick<
      InactivityLogoutConfig,
      | 'warningAfterMs'
      | 'logoutAfterWarningMs'
      | 'pauseWhenHidden'
      | 'isEnabled'
    >
  > & { onLogout: () => void } = {
    warningAfterMs:
      this.injectedConfig?.warningAfterMs ??
      DEFAULT_INACTIVITY_LOGOUT_CONFIG.warningAfterMs,
    logoutAfterWarningMs:
      this.injectedConfig?.logoutAfterWarningMs ??
      DEFAULT_INACTIVITY_LOGOUT_CONFIG.logoutAfterWarningMs,
    pauseWhenHidden:
      this.injectedConfig?.pauseWhenHidden ??
      DEFAULT_INACTIVITY_LOGOUT_CONFIG.pauseWhenHidden,
    isEnabled:
      this.injectedConfig?.isEnabled ??
      DEFAULT_INACTIVITY_LOGOUT_CONFIG.isEnabled,
    onLogout: this.injectedConfig?.onLogout ?? (() => undefined),
  };

  private readonly tabId = this.createTabId();
  private readonly status$ = new BehaviorSubject<InactivityLogoutStatus>(
    'stopped',
  );

  private dialogRef: MatDialogRef<InactivityWarningDialogComponent> | null =
    null;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private mousemoveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private isMonitoring = false;
  private isPausedForVisibility = false;
  private logoutExecuting = false;
  private readonly beforeLogoutHandlers = new Map<
    string,
    InactivityBeforeLogoutHandler
  >();

  private readonly onActivityBound = () => this.recordActivity();
  private readonly onMouseMoveBound = () => this.onMouseMove();
  private readonly onStorageBound = (event: StorageEvent) =>
    this.onStorage(event);
  private readonly onVisibilityBound = () => this.onVisibilityChange();

  readonly status = this.status$.asObservable();

  /**
   * Begins inactivity monitoring when running a production build and enabled.
   */
  start(): void {
    if (!this.config.isEnabled()) {
      return;
    }
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;
    this.status$.next('monitoring');
    this.attachListeners();
    this.recordActivity();
    this.scheduleFromStorage();
  }

  /**
   * Stops monitoring, clears timers, closes any warning dialog, and clears storage.
   */
  stop(): void {
    this.isMonitoring = false;
    this.isPausedForVisibility = false;
    this.status$.next('stopped');
    this.detachListeners();
    this.clearTimers();
    this.closeDialog();
    this.clearStorage();
    this.beforeLogoutHandlers.clear();
  }

  /**
   * Registers an async action to run on this tab before inactivity logout.
   * Handler errors are logged and do not block logout.
   *
   * @param handler - Sync or async work (e.g. save draft).
   * @returns Unregister function; idempotent.
   */
  registerBeforeLogout(handler: InactivityBeforeLogoutHandler): () => void;

  /**
   * Registers an async action under a stable key (replaces any prior handler for that key).
   *
   * @param key - Stable id for the active screen or component.
   * @param handler - Sync or async work (e.g. save draft).
   * @returns Unregister function; idempotent.
   */
  registerBeforeLogout(
    key: string,
    handler: InactivityBeforeLogoutHandler,
  ): () => void;

  registerBeforeLogout(
    keyOrHandler: string | InactivityBeforeLogoutHandler,
    handler?: InactivityBeforeLogoutHandler,
  ): () => void {
    const key =
      typeof keyOrHandler === 'string' ? keyOrHandler : this.createHandlerKey();
    const fn = typeof keyOrHandler === 'string' ? handler! : keyOrHandler;
    this.beforeLogoutHandlers.set(key, fn);
    return () => this.unregisterBeforeLogout(key);
  }

  /**
   * Removes a previously registered before-logout handler.
   *
   * @param key - Key returned from {@link registerBeforeLogout} overload with key.
   */
  unregisterBeforeLogout(key: string): void {
    this.beforeLogoutHandlers.delete(key);
  }

  /**
   * Records user activity, resets idle timers, and dismisses an active warning.
   */
  recordActivity(): void {
    if (
      !this.isMonitoring ||
      this.status$.value === 'warning' ||
      this.isLogoutCommitted()
    ) {
      return;
    }

    const now = Date.now();
    localStorage.setItem(INACTIVITY_STORAGE_KEYS.lastActivityAt, String(now));
    localStorage.removeItem(INACTIVITY_STORAGE_KEYS.warningShownAt);
    localStorage.removeItem(INACTIVITY_STORAGE_KEYS.leaderTabId);
    localStorage.removeItem(INACTIVITY_STORAGE_KEYS.logoutInProgress);

    this.closeDialog();
    this.status$.next('monitoring');
    this.scheduleWarning(now);
  }

  /** Registers DOM activity, storage, and visibility listeners. */
  private attachListeners(): void {
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, this.onActivityBound, true);
    }
    document.addEventListener('mousemove', this.onMouseMoveBound, true);
    window.addEventListener('storage', this.onStorageBound);
    if (this.config.pauseWhenHidden) {
      document.addEventListener('visibilitychange', this.onVisibilityBound);
    }
  }

  /** Removes all listeners registered by {@link attachListeners}. */
  private detachListeners(): void {
    for (const event of ACTIVITY_EVENTS) {
      document.removeEventListener(event, this.onActivityBound, true);
    }
    document.removeEventListener('mousemove', this.onMouseMoveBound, true);
    window.removeEventListener('storage', this.onStorageBound);
    document.removeEventListener('visibilitychange', this.onVisibilityBound);
    if (this.mousemoveDebounceTimer) {
      clearTimeout(this.mousemoveDebounceTimer);
      this.mousemoveDebounceTimer = null;
    }
  }

  /** Debounces mouse movement so it counts as activity at most once per second. */
  private onMouseMove(): void {
    if (this.mousemoveDebounceTimer || this.status$.value === 'warning') {
      return;
    }
    this.mousemoveDebounceTimer = setTimeout(() => {
      this.mousemoveDebounceTimer = null;
      this.recordActivity();
    }, 1000);
  }

  /**
   * Re-syncs timers when another tab updates shared inactivity storage.
   *
   * @param event - Cross-tab `storage` event from `localStorage`.
   */
  private onStorage(event: StorageEvent): void {
    if (!this.isMonitoring || this.isPausedForVisibility) {
      return;
    }
    if (
      event.key !== INACTIVITY_STORAGE_KEYS.lastActivityAt &&
      event.key !== INACTIVITY_STORAGE_KEYS.warningShownAt &&
      event.key !== INACTIVITY_STORAGE_KEYS.logoutInProgress
    ) {
      return;
    }

    this.scheduleFromStorage();
  }

  /**
   * Pauses timers while the document is hidden and resumes from storage when visible.
   */
  private onVisibilityChange(): void {
    if (!this.isMonitoring) {
      return;
    }
    if (document.hidden) {
      this.isPausedForVisibility = true;
      this.clearTimers();
      return;
    }
    this.isPausedForVisibility = false;
    this.scheduleFromStorage();
  }

  /**
   * Derives the next monitor state from `localStorage` (logout claim, warning, or idle).
   */
  private scheduleFromStorage(): void {
    if (!this.isMonitoring || this.isPausedForVisibility) {
      return;
    }

    const logoutOwner = this.getLogoutOwnerTabId();
    if (logoutOwner) {
      this.handleLogoutClaim(logoutOwner);
      return;
    }

    const warningShownAt = this.getWarningShownAt();
    if (warningShownAt !== null) {
      this.enterWarningState(warningShownAt);
      return;
    }

    const lastActivityAt = this.getLastActivityAt() ?? Date.now();
    this.status$.next('monitoring');
    this.closeDialog();
    this.scheduleWarning(lastActivityAt);
  }

  /**
   * Schedules the warning dialog after the configured idle period.
   *
   * @param lastActivityAt - Epoch ms of the last recorded user activity.
   */
  private scheduleWarning(lastActivityAt: number): void {
    this.clearTimers();
    const warningAt = lastActivityAt + this.config.warningAfterMs;
    const delay = Math.max(0, warningAt - Date.now());

    this.warningTimer = setTimeout(() => {
      this.warningTimer = null;
      this.onWarningTimeout();
    }, delay);
  }

  /**
   * Schedules automatic logout after the warning grace period.
   *
   * @param warningShownAt - Epoch ms when the warning state began.
   */
  private scheduleLogout(warningShownAt: number): void {
    this.clearLogoutTimer();
    const logoutAt = warningShownAt + this.config.logoutAfterWarningMs;
    const delay = Math.max(0, logoutAt - Date.now());

    this.logoutTimer = setTimeout(() => {
      this.logoutTimer = null;
      this.onLogoutTimeout();
    }, delay);
  }

  /**
   * Enters warning state when idle threshold is met, or reschedules if activity occurred.
   */
  private onWarningTimeout(): void {
    if (!this.isMonitoring || this.isPausedForVisibility) {
      return;
    }

    const lastActivityAt = this.getLastActivityAt();
    if (lastActivityAt === null) {
      this.recordActivity();
      return;
    }

    if (Date.now() - lastActivityAt < this.config.warningAfterMs) {
      this.scheduleWarning(lastActivityAt);
      return;
    }

    let warningShownAt = this.getWarningShownAt();
    if (warningShownAt === null) {
      warningShownAt = Date.now();
      localStorage.setItem(
        INACTIVITY_STORAGE_KEYS.warningShownAt,
        String(warningShownAt),
      );
      if (!localStorage.getItem(INACTIVITY_STORAGE_KEYS.leaderTabId)) {
        localStorage.setItem(INACTIVITY_STORAGE_KEYS.leaderTabId, this.tabId);
      }
    }

    this.enterWarningState(warningShownAt);
  }

  /**
   * Opens the warning dialog, starts the logout countdown, and syncs status across tabs.
   *
   * @param warningShownAt - Epoch ms when the warning state began.
   */
  private enterWarningState(warningShownAt: number): void {
    this.clearWarningTimer();
    this.status$.next('warning');
    this.openDialog(warningShownAt);
    this.scheduleLogout(warningShownAt);

    if (Date.now() >= warningShownAt + this.config.logoutAfterWarningMs) {
      this.onLogoutTimeout();
    }
  }

  /**
   * Claims logout when the grace period expires, unless activity reset the session.
   */
  private onLogoutTimeout(): void {
    if (!this.isMonitoring) {
      return;
    }

    const warningShownAt = this.getWarningShownAt();
    if (warningShownAt === null) {
      this.scheduleFromStorage();
      return;
    }

    const lastActivityAt = this.getLastActivityAt();
    if (lastActivityAt !== null && lastActivityAt > warningShownAt) {
      this.recordActivity();
      return;
    }

    if (Date.now() < warningShownAt + this.config.logoutAfterWarningMs) {
      this.scheduleLogout(warningShownAt);
      return;
    }

    this.claimLogout();
  }

  /** Marks this tab as logout owner in `localStorage` and coordinates cross-tab logout. */
  private claimLogout(): void {
    if (!localStorage.getItem(INACTIVITY_STORAGE_KEYS.logoutInProgress)) {
      localStorage.setItem(
        INACTIVITY_STORAGE_KEYS.logoutInProgress,
        this.tabId,
      );
    }
    const owner = this.getLogoutOwnerTabId();
    if (owner) {
      this.handleLogoutClaim(owner);
    }
  }

  /**
   * Coordinates cross-tab logout; runs before-logout handlers on the owning tab only.
   *
   * @param ownerTabId - Tab id stored in `logoutInProgress`.
   */
  private handleLogoutClaim(ownerTabId: string): void {
    if (this.logoutExecuting) {
      return;
    }
    void this.executeLogout(ownerTabId);
  }

  /**
   * Awaits registered handlers (owner tab), stops monitoring, then calls `onLogout`.
   *
   * @param ownerTabId - Tab id stored in `logoutInProgress`.
   */
  private async executeLogout(ownerTabId: string): Promise<void> {
    this.logoutExecuting = true;
    this.lockWarningDialog();
    try {
      const shouldLogout = ownerTabId === this.tabId;
      if (shouldLogout) {
        await this.runBeforeLogoutHandlers();
      }
      this.stop();
      if (shouldLogout) {
        this.config.onLogout();
      }
    } finally {
      this.logoutExecuting = false;
    }
  }

  /**
   * Runs all registered before-logout handlers in parallel.
   * Failures are logged; logout still proceeds afterward.
   */
  private async runBeforeLogoutHandlers(): Promise<void> {
    const handlers = [...this.beforeLogoutHandlers.values()];
    await Promise.all(
      handlers.map(async (handler) => {
        try {
          await Promise.resolve(handler());
        } catch (error) {
          console.error(
            '[InactivityLogoutService] before-logout handler failed',
            error,
          );
        }
      }),
    );
  }

  /**
   * Opens the inactivity warning dialog if one is not already open.
   *
   * @param warningShownAt - Epoch ms when the warning state began.
   */
  private openDialog(warningShownAt: number): void {
    if (this.dialogRef) {
      return;
    }

    this.dialogRef = this.dialog.open(InactivityWarningDialogComponent, {
      width: '550px',
      data: {
        warningShownAt,
        warningAfterMs: this.config.warningAfterMs,
        logoutAfterWarningMs: this.config.logoutAfterWarningMs,
        onContinue: () => {
          if (this.isLogoutCommitted()) {
            return;
          }
          this.status$.next('monitoring');
          this.recordActivity();
        },
      },
      disableClose: true,
      autoFocus: false,
      panelClass: ['et-dialog-radius', 'et-dialog-padding'],
    });

    if (this.isLogoutCommitted()) {
      this.lockWarningDialog();
    }

    this.dialogRef.afterClosed().subscribe(() => {
      this.dialogRef = null;
    });
  }

  /**
   * @returns Whether logout has been claimed locally or in shared storage.
   */
  private isLogoutCommitted(): boolean {
    return this.logoutExecuting || !!this.getLogoutOwnerTabId();
  }

  /** Disables Continue and shows the ending-session state on the open warning dialog. */
  private lockWarningDialog(): void {
    this.dialogRef?.componentInstance?.lock();
  }

  /** Closes the warning dialog when present. */
  private closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
  }

  /** Clears both warning and logout timers. */
  private clearTimers(): void {
    this.clearWarningTimer();
    this.clearLogoutTimer();
  }

  /** Clears the idle-to-warning timer. */
  private clearWarningTimer(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /** Clears the post-warning logout timer. */
  private clearLogoutTimer(): void {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  /** Removes all inactivity keys from `localStorage`. */
  private clearStorage(): void {
    localStorage.removeItem(INACTIVITY_STORAGE_KEYS.lastActivityAt);
    localStorage.removeItem(INACTIVITY_STORAGE_KEYS.warningShownAt);
    localStorage.removeItem(INACTIVITY_STORAGE_KEYS.leaderTabId);
    localStorage.removeItem(INACTIVITY_STORAGE_KEYS.logoutInProgress);
  }

  /**
   * @returns Parsed `lastActivityAt` from storage, or `null` if missing or invalid.
   */
  private getLastActivityAt(): number | null {
    const raw = localStorage.getItem(INACTIVITY_STORAGE_KEYS.lastActivityAt);
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * @returns Parsed `warningShownAt` from storage, or `null` if missing or invalid.
   */
  private getWarningShownAt(): number | null {
    const raw = localStorage.getItem(INACTIVITY_STORAGE_KEYS.warningShownAt);
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * @returns Tab id that claimed logout, or `null` if none.
   */
  private getLogoutOwnerTabId(): string | null {
    return localStorage.getItem(INACTIVITY_STORAGE_KEYS.logoutInProgress);
  }

  /**
   * @returns A unique id for this browser tab, used for cross-tab logout coordination.
   */
  private createTabId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private createHandlerKey(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `handler-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
